import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { financeClient, assetUrl } from '../api/financeClient';
import { dateTimeStr } from '../format';

// The single shared real-estate site-visit popup (spec Part C). Self-contained: it renders a
// compact status block + full detail, and — because the module cannot import the real_estate app's
// <VisitActions> without breaking the isolation contract — it takes the action strip via a
// `renderActions(visit)` render-prop. The host passes it its real <VisitActions> (already
// role-agnostic; every backing route authorises admin) when the actor is owner/admin, exactly how
// OrderDetailsModal renders BookingActions in a bordered block at the top.
const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

function agentLabel(assignedTo) {
  if (!assignedTo || typeof assignedTo !== 'object') return null;
  return `${assignedTo.name || '—'}${assignedTo.phone ? ` · ${assignedTo.phone}` : ''}`;
}

export default function VisitDetailsModal({ visit, show, onHide, renderActions, onOpenStaff }) {
  const [full, setFull] = useState(visit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    if (!show || !visit) return undefined;
    setShowMore(false);
    setActivePhoto(0);
    const id = visit.visitId || visit.bookingId || visit._id;
    // Treat as sparse when the visit lacks propertyImages — it may have come from a list endpoint
    // that wasn't enriched yet (e.g. an older deploy). The single-visit endpoint always enriches.
    const sparse = !visit.customerName || !visit.startDate || !('propertyImages' in visit);
    if (!sparse) {
      setFull(visit);
      setError('');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFull(visit);
    setError('');
    setLoading(true);
    financeClient.get(`/api/real_estate/visits/${id}`)
      .then((res) => { if (!cancelled) setFull(res.data?.data || res.data); })
      .catch(() => { if (!cancelled) setError('Could not load this visit.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, visit]);

  if (!visit) return null;
  const v = full || visit;
  const agent = v.deliveryAssignment?.assignedTo;

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Visit #{String(v._id).slice(-6).toUpperCase()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" role="status" className="mb-2" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {typeof renderActions === 'function' && !loading && (
          <div className="mb-3 pb-3 border-bottom">
            {renderActions(v)}
          </div>
        )}

        <div className="d-flex flex-wrap gap-1 mb-2">
          <Badge bg={STATUS_VARIANT[v.status] || 'secondary'}>{v.status}</Badge>
          {v.deliveryNeedsReassignment && (
            <Badge bg="warning" text="dark">Agent declined — needs reassignment</Badge>
          )}
        </div>

        <p className="mb-1"><strong>{v.customerName}</strong></p>
        <p className="text-muted small mb-2">{[v.email, v.phoneNumber].filter(Boolean).join(' · ')}</p>
        <p className="mb-1">{dateTimeStr(v.startDate || v.visitDate)}</p>

        {(v.itemName || v.propertyName) && (
          <p className="mb-1">Property: {v.itemName || v.propertyName}{(v.itemLocation || v.propertyLocation) ? ` — ${v.itemLocation || v.propertyLocation}` : ''}</p>
        )}
        {v.ownerName && (
          <p className="mb-1">
            Owner: {v.ownerName}
            {(v.ownerEmail || v.ownerPhone) && <> · {[v.ownerEmail, v.ownerPhone].filter(Boolean).join(' · ')}</>}
          </p>
        )}

        {agentLabel(agent) && (
          <p className="mb-1">
            Agent:{' '}
            {onOpenStaff && agent?._id ? (
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onOpenStaff(agent._id)}>
                {agentLabel(agent)}
              </button>
            ) : agentLabel(agent)}
            {v.deliveryAssignment?.scheduledAt && ` · ${dateTimeStr(v.deliveryAssignment.scheduledAt)}`}
          </p>
        )}

        {Array.isArray(v.declineHistory) && v.declineHistory.length > 0 && (
          <div className="small text-muted mt-2">
            Decline history:
            <ul className="mb-0">
              {v.declineHistory.map((d, i) => (
                <li key={i}>{d.by || d.agentName || 'agent'}{d.reason ? ` — "${d.reason}"` : ''}{d.at ? ` (${dateTimeStr(d.at)})` : ''}</li>
              ))}
            </ul>
          </div>
        )}

        {v.notes && <p className="mb-1 text-muted small mt-2">Notes: {v.notes}</p>}
        {v.status === 'cancelled' && v.cancellation?.reason && (
          <p className="small text-muted mt-1">
            Cancelled by {v.cancellation.cancelledBy}
            {v.cancellation.cancelledAt && ` on ${dateTimeStr(v.cancellation.cancelledAt)}`}: "{v.cancellation.reason}"
          </p>
        )}

        {(v.propertyImages?.length > 0 || v.propertySnippet) && (
          <div className="mt-3 pt-2 border-top">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={() => setShowMore((s) => !s)}
            >
              {showMore ? '▲ Hide property details' : '▼ Show property photos & details'}
            </Button>

            {showMore && (
              <PropertyPanel
                images={v.propertyImages || []}
                snippet={v.propertySnippet}
                activePhoto={activePhoto}
                onPhotoSelect={setActivePhoto}
              />
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

// Uploaded images are stored as _1024.webp; substitute a smaller variant when available.
function variantUrl(src, size) {
  if (!src) return '';
  return assetUrl(src.replace(/_1024\.webp$/, `_${size}.webp`));
}

function PropertyPanel({ images, snippet, activePhoto, onPhotoSelect }) {
  const photos = (images || []).filter(Boolean);
  const safe = Math.min(activePhoto, Math.max(0, photos.length - 1));

  return (
    <div className="mt-2">
      {photos.length > 0 && (
        <>
          <div
            className="rounded overflow-hidden mb-2"
            style={{ background: '#000', position: 'relative' }}
          >
            <img
              src={variantUrl(photos[safe], 640)}
              alt={`Property photo ${safe + 1} of ${photos.length}`}
              style={{ width: '100%', maxHeight: 240, objectFit: 'contain', display: 'block' }}
            />
            {photos.length > 1 && (
              <span
                className="position-absolute bottom-0 end-0 m-2 badge bg-dark bg-opacity-75"
                style={{ fontSize: '0.7rem' }}
              >
                {safe + 1} / {photos.length}
              </span>
            )}
          </div>

          {photos.length > 1 && (
            <div
              className="d-flex gap-2 mb-2"
              style={{ overflowX: 'auto', paddingBottom: 4 }}
            >
              {photos.map((src, i) => (
                <img
                  key={src}
                  src={variantUrl(src, 320)}
                  alt={`Thumbnail ${i + 1}`}
                  onClick={() => onPhotoSelect(i)}
                  style={{
                    width: 56, height: 56, objectFit: 'cover', borderRadius: 4,
                    flexShrink: 0, cursor: 'pointer',
                    outline: i === safe ? '2px solid var(--bs-primary)' : '1px solid #dee2e6'
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {snippet && (
        <div className="small text-muted">
          {(snippet.price != null || snippet.priceUnit) && (
            <div className="mb-1 fw-semibold text-body">
              {snippet.price != null ? `₹${snippet.price}` : ''}{snippet.priceUnit ? `/${snippet.priceUnit}` : ''}
              {snippet.category ? <span className="fw-normal ms-2">{snippet.category}</span> : null}
            </div>
          )}
          {[
            snippet.bedrooms != null && `${snippet.bedrooms} bd`,
            snippet.bathrooms != null && `${snippet.bathrooms} ba`,
            snippet.areaSqft != null && `${snippet.areaSqft} sqft`
          ].filter(Boolean).join(' · ') && (
            <div className="mb-1">
              {[
                snippet.bedrooms != null && `${snippet.bedrooms} bd`,
                snippet.bathrooms != null && `${snippet.bathrooms} ba`,
                snippet.areaSqft != null && `${snippet.areaSqft} sqft`
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {snippet.amenities?.length > 0 && (
            <div className="mb-1">{snippet.amenities.join(' · ')}</div>
          )}
          {snippet.description && (
            <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{snippet.description}</p>
          )}
        </div>
      )}
    </div>
  );
}
